import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { Badge } from '@terra/ui';

import { api } from '../lib/api';

interface LedgerTableProps {
  entityId: string;
  type: 'customer' | 'vendor';
}

function formatPaise(paise: number) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(paise / 100);
}

export function WalletLedgerTable({ entityId, type }: LedgerTableProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['ledger', type, entityId],
    queryFn: () =>
      type === 'customer'
        ? api.wallet.adminGetCustomerLedger(entityId)
        : api.wallet.adminGetVendorLedger(entityId),
    enabled: !!entityId,
  });

  if (isLoading) {
    return (
      <div className="text-ink-muted py-8 text-center text-[13px] font-medium">
        Loading ledger...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-ink-muted py-8 text-center text-[13px] font-medium">
        No ledger transactions found.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13px]">
        <thead>
          <tr className="border-border/60 border-b">
            <th className="text-ink-subtle px-4 py-3 font-semibold">Date</th>
            <th className="text-ink-subtle px-4 py-3 font-semibold">Type</th>
            <th className="text-ink-subtle px-4 py-3 font-semibold">Account</th>
            <th className="text-ink-subtle px-4 py-3 text-right font-semibold">Debit</th>
            <th className="text-ink-subtle px-4 py-3 text-right font-semibold">Credit</th>
          </tr>
        </thead>
        <tbody className="divide-border/60 divide-y">
          {data.map((entry: any) => (
            <tr key={entry.id} className="hover:bg-ink/5 transition-colors">
              <td className="px-4 py-3 align-top">
                <div className="text-ink font-medium">
                  {new Date(entry.createdAt).toLocaleDateString()}
                </div>
                <div className="text-ink-muted text-[11px]">
                  {new Date(entry.createdAt).toLocaleTimeString()}
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className="text-ink font-bold">{entry.kind}</div>
                {(entry.refType || entry.refId) && (
                  <div className="text-ink-subtle mt-0.5 font-mono text-[11px]">
                    {entry.refType} {entry.refId}
                  </div>
                )}
                {entry.memo && <div className="text-ink-subtle mt-1 text-[12px]">{entry.memo}</div>}
              </td>
              <td className="px-4 py-3 align-top">
                <Badge tone="neutral">{entry.accountCode}</Badge>
              </td>
              <td className="px-4 py-3 text-right align-top font-medium text-red-600">
                {entry.debitPaise > 0 ? formatPaise(entry.debitPaise) : '-'}
              </td>
              <td className="px-4 py-3 text-right align-top font-medium text-emerald-600">
                {entry.creditPaise > 0 ? formatPaise(entry.creditPaise) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
